import type { DownloadsRankingPeriod } from '../types'
import type { SortFilterValue } from '../../filters/filters'

// The production index currently exposes only year and month as sortable ranking fields.
// Week and day use the closest supported period so changing the toolbox never invalidates the search.
const popularitySortFieldByPeriod: Record<DownloadsRankingPeriod, string> = {
  year: 'downloads_ranking.year',
  month: 'downloads_ranking.month',
  week: 'downloads_ranking.month',
  day: 'downloads_ranking.month',
}

/**
 * Builds the Page Key core indexes Page Priorities under: the ids of the page's node and its
 * ancestors, root first, joined with two underscores (e.g. `vfx__visual-graphics`).
 */
export function buildPageKey(nodeIds: string[]): string {
  return nodeIds.join('__')
}

/**
 * Builds the Meilisearch sort array. An explicit sort wins; otherwise, without a text query,
 * assets sort by the Page Priority of each Page Key in turn (when per-Page ordering is on) and
 * then by the global priority. A Content page passes its own key and then its Subcategory's, so a
 * Content no curator ordered falls back to its Subcategory's order. Assets with no value for a
 * field sort after those that have one.
 */
export function buildSortQuery({
  sort,
  hasTextQuery,
  perPageOrdering,
  pageKeys,
  popularityRankingPeriod,
}: {
  sort?: SortFilterValue
  hasTextQuery: boolean
  perPageOrdering: boolean
  pageKeys: string[]
  popularityRankingPeriod?: DownloadsRankingPeriod
}): string[] {
  switch (sort) {
    case 'new':
      return ['created_at_timestamp:desc']
    case 'popular':
      return [`${popularitySortFieldByPeriod[popularityRankingPeriod ?? 'year']}:desc`]
    case 'trending':
      return ['downloads_ranking.month:desc']
  }

  if (hasTextQuery) {
    return []
  }

  return perPageOrdering
    ? [...pageKeys.map((pageKey) => `priorities.${pageKey}:desc`), 'priority:desc']
    : ['priority:desc']
}
